import React, { useMemo, useCallback, useState } from "react";
import { Animate } from "react-show";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import styled from "styled-components";
import { NotificationEntity } from "src/firebase/db/Notifications";
import {
  OrderBreadProps,
  orderBreadInitialEntity,
} from "src/firebase/db/OrderBreads";

import { formatDate } from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";

dayjs.extend(isSameOrBefore);

type Props = {
  notification: NotificationEntity;
  onCancel: (notificationId: string) => void;
};

const ReservationHistoryListItem = (props: Props) => {
  const {
    notification: {
      id,
      is_draft,
      is_published,
      is_notified,
      last_pickup_days,
      published_time,
      scheduled_time,
      order_breads,
    },
    onCancel,
  } = props;
  const [showDetail, setShowDetail] = useState(false);

  const orderBread = useMemo(() => {
    if (!order_breads.length) {
      return {
        ...orderBreadInitialEntity,
        original_orders: [],
      };
    }
    return order_breads[0] as OrderBreadProps;
  }, [order_breads]);

  const lastPickupDays = useMemo(() => {
    return last_pickup_days as (Date | undefined)[];
  }, [last_pickup_days]);

  const { name, stock, original_orders } = orderBread;

  const isPublished = useMemo(
    () => !is_draft && (is_published || is_notified),
    [is_draft, is_published, is_notified]
  );

  const soldQuntity = useMemo(
    () => (original_orders ? original_orders.length : 0),
    [original_orders]
  );

  const wrapOnCancel = useCallback(() => {
    if (!isPublished) {
      onCancel(id);
    }
  }, [id, isPublished, onCancel]);

  return (
    <ListItem>
      <div className="dates">
        <div className="published">
          <span>事前告知配信：</span>
          <span>{formatDate(published_time, "YYYY年MM月DD日(ddd) HH:mm")}</span>
        </div>
        <div className="scheduled">
          <span>予約注文開始日時：</span>
          <span>{formatDate(scheduled_time, "YYYY年MM月DD日(ddd) HH:mm")}</span>
        </div>
      </div>
      <div className="detail">
        <div className="detail-header">
          <div className="title">{name}</div>
          <div className="status">
            <div className="count">
              <span>現時点受注数：</span>
              <span>
                {soldQuntity}個／{stock}個
              </span>
            </div>
            <div className="action">
              <button
                className={`button ${isPublished ? "is-published" : ""}`}
                onClick={wrapOnCancel}
              >
                {isPublished ? "配信済み" : "配信をキャンセルする"}
              </button>
            </div>
          </div>
        </div>
        <div className="detail-body">
          <div className="pickup-days">
            <h4>受け渡し日</h4>
            {lastPickupDays.length > 0 && (
              <button onClick={() => setShowDetail(!showDetail)}>
                {showDetail ? "↑詳細を閉じる" : "↓詳細を見る"}
              </button>
            )}
          </div>
          <Animate
            show={showDetail}
            duration={500}
            style={{
              height: "auto",
              background: "white",
              overflow: "hidden",
            }}
            start={{
              opacity: 0,
              height: 0,
            }}
            enter={{
              opacity: 1,
              height: "auto",
            }}
            leave={{
              opacity: 0,
              height: 0,
            }}
          >
            <ul>
              {lastPickupDays.map((v, i) => (
                <li key={`${id}|${i}`}>
                  <span>{formatDate(v, "YYYY年MM月DD日(ddd)")}</span>
                </li>
              ))}
            </ul>
          </Animate>
        </div>
      </div>
    </ListItem>
  );
};

const ListItem = styled.div`
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  line-height: ${20 / 16};

  .dates {
    .published {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 8px;
      color: ${StyleConfig.COLOR.GREEN};
    }

    .scheduled {
      font-size: 14px;
      margin-bottom: 12px;

      span {
        &:first-child {
          color: ${StyleConfig.COLOR.TEXT_GRAY};
        }
      }
    }
  }

  .detail {
    background-color: #fff;
    margin-bottom: 32px;
    width: 100%;
    padding: 20px;

    .detail-header {
      margin-bottom: 24px;

      .title {
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 10px;
      }

      .status {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .count {
          font-size: 14px;
          span {
            &:first-child {
              color: ${StyleConfig.COLOR.TEXT_GRAY};
            }
            &:last-child {
              font-weight: bold;
            }
          }
        }
      }

      .action {
        button {
          font-weight: bold;
          color: white;
          padding: 6px 10px;
          border-radius: 14px;
          background-color: #ff0303;

          &.is-published {
            background-color: #bababa;
            cursor: text;
          }
        }
      }
    }

    .detail-body {
      .pickup-days {
        display: flex;
        justify-content: space-between;
      }

      button {
        margin-bottom: 10px;
      }

      h4 {
        font-size: 12px;
        font-weight: bold;
        margin-bottom: 12px;
      }

      ul {
        li {
          position: relative;
          color: ${StyleConfig.COLOR.GREEN};
          padding: 16px 0 16px 14px;
          border-top: 1px solid #bababa;

          &:last-child {
            border-bottom: 1px solid #bababa;
          }

          &::before {
            content: url(/images/icon/dot_g.svg);
            display: inline-block;
            position: absolute;
            top: 45%;
            left: 0%;
            transform: translateY(-50%);
          }

          span {
            color: ${StyleConfig.COLOR.TEXT_COLOR};
          }
        }
      }
    }
  }
`;

export default ReservationHistoryListItem;
